import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL, FALLBACK_API_URL } from '../config';

const PlacementHRInterviewPage = () => {
  const navigate = useNavigate();
  const { assessmentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [interviewConfig, setInterviewConfig] = useState(null);
  const [isResuming, setIsResuming] = useState(false);

  const session = SessionManager.getSession('candidate');
  const apiUrl = API_BASE_URL || FALLBACK_API_URL;

  useEffect(() => {
    if (!session) {
      navigate('/login');
      return;
    }
    startInterview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const startInterview = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${apiUrl}/placement-training/interview/start`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            assessment_id: assessmentId,
            interview_type: 'hr'
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setInterviewConfig(data);
        setIsResuming(data.is_resuming || false);
      } else {
        alert('Failed to start interview. Please try again.');
        navigate(`/placement-training/assessment/${assessmentId}`);
      }
    } catch (error) {
      console.error('Error starting interview:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !interviewConfig) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center">
              <Icon name="people" size={32} className="text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">HR Interview</h1>
              <p className="text-gray-600">
                {interviewConfig.company_name} - {interviewConfig.position_title}
              </p>
            </div>
          </div>

          {isResuming && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-700">
                <Icon name="info" size={20} />
                <span className="font-medium">
                  Resuming interview - The AI will continue from where you left off
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Interview Instructions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Interview Guidelines</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Icon name="schedule" size={20} className="text-green-600 mt-0.5" />
              <div>
                <div className="font-medium text-gray-900">Duration</div>
                <div className="text-sm text-gray-600">
                  {interviewConfig.duration_minutes} minutes total
                  {isResuming && ` (${Math.floor(interviewConfig.time_remaining_seconds / 60)} minutes remaining)`}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Icon name="mic" size={20} className="text-green-600 mt-0.5" />
              <div>
                <div className="font-medium text-gray-900">Voice Interview</div>
                <div className="text-sm text-gray-600">
                  Speak confidently and showcase your personality and soft skills
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Icon name="psychology" size={20} className="text-green-600 mt-0.5" />
              <div>
                <div className="font-medium text-gray-900">AI HR Interviewer</div>
                <div className="text-sm text-gray-600">
                  The AI will ask behavioral and situational questions to assess your fit
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Icon name="verified" size={20} className="text-green-600 mt-0.5" />
              <div>
                <div className="font-medium text-gray-900">Evaluation</div>
                <div className="text-sm text-gray-600">
                  Your responses will be evaluated for communication, attitude, and cultural fit
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Common HR Questions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Common HR Topics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              'Tell me about yourself',
              'Strengths and weaknesses',
              'Career goals',
              'Why this company?',
              'Handling challenges',
              'Teamwork experience',
              'Leadership examples',
              'Work-life balance'
            ].map((topic, idx) => (
              <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <Icon name="check_circle" size={18} className="text-green-600" />
                <span className="text-sm text-gray-700">{topic}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Start Interview Button */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-2">
            {isResuming ? 'Resume HR Interview' : 'Final Round!'}
          </h3>
          <p className="text-green-100 mb-6">
            {isResuming 
              ? 'Continue your interview from where you left off'
              : 'Complete this final round to finish your assessment'
            }
          </p>
          <button
            onClick={() => {
              // Navigate to live interview page with session ID
              navigate(`/live-interview/${interviewConfig.interview_session_id}`, {
                state: {
                  type: 'placement_hr',
                  interviewType: 'placement_hr',  // For WebSocket
                  assessmentId,
                  roundId: interviewConfig.round_id,
                  duration: interviewConfig.duration_minutes,
                  timeRemaining: interviewConfig.time_remaining_seconds,
                  isResuming,
                  candidateName: session?.user?.name || 'Candidate',
                  jobTitle: interviewConfig.position_title || 'Position',
                  company: interviewConfig.company_name || 'Company',
                  language: 'en-US'
                }
              });
            }}
            className="px-8 py-4 bg-white text-green-600 rounded-xl font-semibold hover:bg-gray-100 transition-colors inline-flex items-center gap-2"
          >
            <Icon name={isResuming ? 'play_arrow' : 'mic'} size={24} />
            <span>{isResuming ? 'Resume Interview' : 'Start Interview'}</span>
          </button>
        </div>

        {/* Tips */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <Icon name="lightbulb" size={24} className="text-blue-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">HR Interview Tips</h3>
              <ul className="space-y-1 text-sm text-blue-800">
                <li>• Be authentic and genuine in your responses</li>
                <li>• Use the STAR method (Situation, Task, Action, Result) for behavioral questions</li>
                <li>• Show enthusiasm for the role and company</li>
                <li>• Highlight your soft skills and teamwork abilities</li>
                <li>• Ask thoughtful questions about the company culture</li>
                <li>• Maintain a positive attitude throughout</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlacementHRInterviewPage;

